import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useOnErrorAuto, useUser } from '@/utils/hooks'
import type {
  ChecklistsType,
  ChecklistType,
  ChecklistEntries,
} from '@/modules/checklists/types'
import type {
  PatchChecklistItem,
  BatchChangeChecklistItemsMutationVariables,
  BatchChangeChecklistItemsMutation,
  AllChecklistsQuery,
} from '@/modules/checklists/types.graphql'
import { Checklist } from '@/modules/checklists/components/CheckList'

import { useMutation } from '@apollo/client'
import { BATCH_CHANGE_CHECKLIST_ITEMS } from './mutations'
import { DetectBlur } from '@/containers/util-containers'
import { useForm } from '@ur-solutions/react-hooks'
import { useUserChecklists } from './hooks'
import { transformChecklistQueryData } from './util'

interface TodaysChecklistsProps {
  onEmptyCallback: (isEmpty: boolean) => void
}

export const TodaysChecklists: React.FC<TodaysChecklistsProps> = ({
  onEmptyCallback,
}) => {
  const onErrorAuto = useOnErrorAuto()

  const [dirtyChecklistItems, setDirtyChecklistItems] = useState<
    PatchChecklistItem[]
  >([])

  const [batchPatchMutation] = useMutation<
    BatchChangeChecklistItemsMutation,
    BatchChangeChecklistItemsMutationVariables
  >(BATCH_CHANGE_CHECKLIST_ITEMS, {
    refetchQueries: ['AllChecklists'],
    onCompleted() {
      setDirtyChecklistItems([])
    },
    onError: onErrorAuto(),
  })
  const user = useUser()

  const handleSubmit = useCallback(() => {
    if (dirtyChecklistItems.length > 0) {
      void batchPatchMutation({
        variables: {
          updatedChecklistItems: dirtyChecklistItems,
        },
      })
    }
  }, [dirtyChecklistItems, batchPatchMutation])

  const { formValues, updateForm } = useForm<ChecklistsType>({
    values: {},
  })

  const handleChecklistsLoaded = (data: AllChecklistsQuery) => {
    const queryData = transformChecklistQueryData(data)
    updateForm(queryData)
  }

  const { userChecklists: data, loading } = useUserChecklists(
    user,
    handleChecklistsLoaded
  )

  useEffect(() => {
    window.addEventListener('beforeunload', handleSubmit)
    return () => {
      window.removeEventListener('beforeunload', handleSubmit)
    }
  }, [handleSubmit])

  const showChecklists = useMemo(
    () =>
      data?.allChecklists &&
      data.allChecklists.edges.filter(edge => edge.node.active).length > 0,
    [data]
  )

  useEffect(() => {
    if (!data) return
    if (!showChecklists) return onEmptyCallback(true)
    onEmptyCallback(false)
  }, [data, showChecklists, onEmptyCallback])

  function onChange(
    checklist: ChecklistType,
    checklistItem: PatchChecklistItem,
    newValue: ChecklistEntries
  ) {
    updateForm({
      [checklist.id]: {
        id: checklist.id,
        title: checklist.title,
        entries: newValue,
        active: checklist.active,
      },
    })
    if (
      dirtyChecklistItems.filter(dirtyItem => dirtyItem.id === checklistItem.id)
        .length === 0
    ) {
      setDirtyChecklistItems(dirtyChecklistItems.concat([checklistItem]))
    }
  }

  return (
    <DetectBlur
      disableTouchEnd
      active={dirtyChecklistItems.length > 0}
      onClickOutside={() => handleSubmit()}
    >
      {showChecklists &&
        !loading &&
        data &&
        Object.entries(formValues).map(([checklistId, checklist]) => (
          <Checklist
            key={checklistId}
            checklist={checklist}
            onChange={onChange}
            onSubmit={handleSubmit}
            mandatory={false}
            removeChecklistFromForm={() => {
              updateForm({ [checklistId]: null })
            }}
          />
        ))}
    </DetectBlur>
  )
}
